import { useLocalStorage } from "@elit-hair/elit-tools";
import { SidebarInset, SidebarProvider, useIsMobile } from "@elit-hair/elit-ui";
import { Outlet, useMatches } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo } from "react";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { cn } from "@/lib/utils";
import { useSettings } from "@/providers/general/settings-provider";
import { PageActionsProvider } from "@/providers/hooks/use-page-actions";
import { PageFiltersProvider } from "@/providers/hooks/use-page-filters";
import { PageTitleOverrideProvider } from "@/providers/hooks/use-page-title-override";

import { CallDock } from "./call-dock/call-dock";
import { ElitphoneUserEmailMeta } from "./elitphone-user-email-meta";
import { Header } from "./header";
import { PageToolbar } from "./page-toolbar";
import { SidebarLeft } from "./sidebar/sidebar-left";
import { SidebarRight } from "./sidebar/sidebar-right";

/**
 * Application shell layout that renders the left and right sidebars, header, main content outlet, and call dock.
 *
 * Left sidebar open state is synced with remote config (SIDEBAR_COLLAPSED). Right sidebar stays in local storage.
 * Registers global keyboard shortcuts: Cmd/Ctrl+B toggles the left sidebar and Cmd/Ctrl+I toggles the right sidebar when not in mobile view.
 *
 * @returns The layout JSX element containing sidebar providers, Header, main content Outlet, and CallDock.
 */
export function Layout() {
    const { currentSettings, setValue } = useSettings();

    const leftSidebarOpen = !currentSettings.SIDEBAR_COLLAPSED;
    const setLeftSidebarOpen = useCallback(
        (value: boolean | ((prev: boolean) => boolean)) => {
            const nextOpen = typeof value === "function" ? value(leftSidebarOpen) : value;
            setValue(SETTINGS_KEYS.SIDEBAR_COLLAPSED, !nextOpen);
        },
        [leftSidebarOpen, setValue]
    );

    const [rightSidebarOpen, setRightSidebarOpen] = useLocalStorage<boolean>(
        LOCAL_STORAGE_KEYS.RIGHT_SIDEBAR_OPEN.key,
        LOCAL_STORAGE_KEYS.RIGHT_SIDEBAR_OPEN.default
    );

    const isMobile = useIsMobile();

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Left sidebar: Cmd/Ctrl + B
            if (event.key === "b" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                if (!isMobile) {
                    setLeftSidebarOpen((prev) => !prev);
                }
            }
            // Right sidebar: Cmd/Ctrl + I
            if (event.key === "i" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                if (!isMobile) {
                    setRightSidebarOpen((prev) => !prev);
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isMobile, setLeftSidebarOpen, setRightSidebarOpen]);

    const handleToggleRightSidebar = (value: boolean) => {
        setRightSidebarOpen(value);
    };

    const matches = useMatches();
    const disablePageScroll = useMemo(
        () => matches.some((match) => match.staticData?.config?.layout?.scroll === false),
        [matches]
    );

    return (
        <SidebarProvider
            open={leftSidebarOpen}
            onOpenChange={setLeftSidebarOpen}
            className="h-dvh max-h-dvh overflow-hidden"
        >
            <ElitphoneUserEmailMeta />
            {!isMobile && <SidebarLeft />}
            <SidebarInset className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <PageTitleOverrideProvider>
                    <Header
                        onToggleRightSidebar={() => handleToggleRightSidebar(!rightSidebarOpen)}
                        showRightSidebarToggle={true}
                    />
                    <PageFiltersProvider>
                        <PageActionsProvider>
                            <div className="flex h-0 min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4 max-w-full">
                                <PageToolbar />
                                <div
                                    className={cn(
                                        "flex h-0 min-h-0 flex-1 flex-col",
                                        disablePageScroll ? "overflow-hidden" : "overflow-auto"
                                    )}
                                >
                                    <Outlet />
                                </div>
                            </div>
                        </PageActionsProvider>
                    </PageFiltersProvider>
                </PageTitleOverrideProvider>
            </SidebarInset>
            <SidebarProvider
                open={rightSidebarOpen}
                onOpenChange={handleToggleRightSidebar}
                disableKeyboardShortcut={true}
                mobileSheetTitle="right_panel"
                mobileSheetDescription="right_sidebar_mobile_description"
                className="w-auto h-full shrink-0 bg-transparent"
            >
                <SidebarRight />
            </SidebarProvider>
            <CallDock />
        </SidebarProvider>
    );
}
